import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from './components/Footer';
import AdminNavigation from './components/AdminNavigation';
import PullToRefresh from './components/PullToRefresh';
import SharedWithMePanel from './components/SharedWithMePanel';
import { useOnlineOnly } from './components/OfflineHint';
import { getApiUrl } from './config';
import { getAccessToken, logout } from './utils/auth';
import * as repository from './utils/offline/repository';

function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [notAvailableOffline, setNotAvailableOffline] = useState(false);
  const offline = useOnlineOnly();

  // Newest first; projects without a created_at sort to the bottom.
  const newestFirst = (list) =>
    list.slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const readCached = useCallback(async () => {
    try {
      return await repository.getProjects();
    } catch (e) {
      return []; // no account bound yet, or the store is unavailable
    }
  }, []);

  /**
   * Render what the device already holds, then refresh from the server.
   *
   * With cached projects on screen, a network failure is not an error state
   * (FR-001, FR-006): the library stays browsable and the offline banner
   * explains why it may be stale.
   */
  const fetchFiles = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      logout();
      return;
    }

    const cached = await readCached();
    setNotAvailableOffline(false);
    if (cached.length > 0) {
      setProjects(newestFirst(cached));
      setError('');
      setLoading(false);
    }

    try {
      const response = await axios.get(getApiUrl('projects'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // `raw` is passed to the cache unmodified on purpose: a response with no
      // `projects` field must not be reconciled as "you have no projects" and
      // prune the library (FR-037). The repository ignores a non-array.
      const raw = response.data.projects;
      const fresh = raw || [];
      setProjects(newestFirst(fresh));
      setError('');
      // Best effort, and deliberately isolated: a storage failure must never
      // be reported to the user as a problem loading their library.
      Promise.resolve()
        .then(() => repository.cacheProjects(raw))
        .catch((e) => console.warn('Dashboard: cache write failed', e));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        logout();
        return;
      }
      if (err.response) {
        setError(`Error: ${err.response.data.message || err.response.statusText}`);
      } else if (cached.length > 0) {
        setError(''); // offline, but the device has the library
      } else if (err.request) {
        // Nothing on the device and no connection: say so, rather than
        // rendering an empty table that reads as "you have no projects".
        setNotAvailableOffline(true);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  }, [readCached]);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    await fetchFiles();
  }, [fetchFiles]);

  const handleCreate = async () => {
    setCreating(true);
    setCreateError('');
    try {
      const token = getAccessToken();
      if (!token) {
        logout();
        return;
      }
      const response = await axios.post(
        getApiUrl('projects'),
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      navigate(`/projects/${response.data.id}/upload`);
    } catch (err) {
      if (err.response) {
        setCreateError(err.response.data?.message || err.response.data?.detail || `Error: ${err.response.statusText}`);
      } else if (err.request) {
        setCreateError('Network error: Could not connect to server.');
      } else {
        setCreateError('An unexpected error occurred.');
      }
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
        <AdminNavigation />

        {/* Main Content */}
        <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Dashboard
              </h1>
            </div>
            <div className="flex flex-col items-end gap-1">
              {!offline && (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                  {creating ? 'Creating…' : 'Create Project'}
                </button>
              )}
              {createError && (
                <p className="text-xs text-red-600 dark:text-red-400">{createError}</p>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading files...
              </span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              {error.includes('login') && (
                <Link
                  to="/login"
                  className="mt-2 inline-block text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                >
                  Go to Login
                </Link>
              )}
            </div>
          )}

          {/* Nothing cached and no connection (FR-004 / US2 scenario 4) */}
          {!loading && notAvailableOffline && (
            <div className="mb-6 rounded-lg border border-gray-200 p-6 text-center dark:border-gray-800">
              <p className="text-gray-700 dark:text-gray-300">
                Your library isn&apos;t available offline yet.
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Connect once and it will be here next time you&apos;re offline.
              </p>
              <button
                type="button"
                onClick={handleRefresh}
                className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          )}

          {/* Projects Table */}
          {!loading && !error && !notAvailableOffline && (
            <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg overflow-hidden">
              {projects.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No projects found
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          My projects
                        </th>
                        <th className="hidden md:table-cell px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date Added
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {projects.map((project) => (
                        <tr
                          key={project.id}
                          onClick={() => navigate(`/projects/${project.id}/chapters`)}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {project.title || 'Untitled'}
                          </td>
                          <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                            {project.created_at
                              ? new Date(project.created_at).toLocaleDateString()
                              : 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Shared with me — backed by GET /shares/received?status=all */}
          {/* Not rendered offline: shares are not cached, so the panel would
              have nothing to show and would ask the server anyway. */}
          {!loading && !error && !notAvailableOffline && !offline && <SharedWithMePanel />}
        </div>

        {/* Footer */}
        <Footer desktopOnly />
      </div>
    </PullToRefresh>
  );
}

export default Dashboard;
